<script setup lang="ts">
import { useEventStore } from '@/stores/event-store.js'
import Event from '@/components/Event.vue'
import MainNav from '@/components/MainNav.vue'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const eventStore = useEventStore()
const router = useRouter()

// Track if we're currently transitioning
const isTransitioning = ref(false)

// Touch handling for swipe gestures
const touchStartX = ref(0)
const touchStartY = ref(0)
const minSwipeDistance = 175
const maxVerticalDistance = 100

watch(
  () => router.currentRoute.value.path,
  (newPath, oldPath) => {
    if (newPath !== oldPath && eventStore.showEvent) {
      // Only close event view if navigating away from event routes
      // Allow navigation TO event routes to keep the event view open
      if (!newPath.startsWith('/event/')) {
        eventStore.showEvent = false
      }
    }
  },
)

const handleTouchStart = (event: TouchEvent) => {
  if (!eventStore.showEvent) return
  touchStartX.value = event.touches[0].clientX
  touchStartY.value = event.touches[0].clientY
}

const handleTouchEnd = (event: TouchEvent) => {
  if (!eventStore.showEvent) return
  const touchEndX = event.changedTouches[0].clientX
  const touchEndY = event.changedTouches[0].clientY
  const deltaX = touchEndX - touchStartX.value
  const deltaY = Math.abs(touchEndY - touchStartY.value)

  if (deltaX > minSwipeDistance && deltaY < maxVerticalDistance) {
    closeEvent()
  }
}

const closeEvent = () => {
  eventStore.showEvent = false
  // Navigate back to timeline if we're on an event route
  if (router.currentRoute.value.name === 'event') {
    router.push('/')
  }
}

// Handle transition events
const handleTransitionStart = () => {
  isTransitioning.value = true
}

const handleTransitionEnd = () => {
  isTransitioning.value = false
}
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Center portion -->
    <main class="flex-1 overflow-hidden">
      <div class="flex w-full h-full">
        <!-- Parent Content -->
        <div
          class="w-full h-full flex-shrink-0 overflow-y-auto transition-transform duration-300 ease-in-out"
          :class="[
            eventStore.showEvent ? '-translate-x-full' : 'translate-x-0',
            isTransitioning ? 'hide-scrollbar' : '',
          ]"
          @transitionstart="handleTransitionStart"
          @transitionend="handleTransitionEnd"
        >
          <router-view />
        </div>

        <!-- Child Content -->
        <div
          v-if="!eventStore.loadingEvent"
          class="w-full h-full flex-shrink-0 overflow-y-auto px-2 transition-transform duration-300 ease-in-out webkit-overflow-scrolling-touch"
          :class="[
            eventStore.showEvent ? '-translate-x-full' : 'translate-x-0',
            isTransitioning ? 'hide-scrollbar' : '',
          ]"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
          @transitionstart="handleTransitionStart"
          @transitionend="handleTransitionEnd"
        >
          <div class="h-full">
            <Event />
          </div>
        </div>

        <!-- Loading overlay -->
        <div
          v-if="eventStore.loadingEvent"
          class="fixed inset-0 z-20 flex justify-center items-center"
        >
          <div class="rounded bg-base-300 w-24 h-24 flex items-center justify-center">
            <div class="loading loading-ring loading-xl"></div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <div class="safe-area-bottom">
      <MainNav />
    </div>
  </div>
</template>

<style scoped>
/* Safe area handling for iOS */
.safe-area-bottom {
  padding-bottom: env(safe-area-inset-bottom);
}

/* Only apply safe area padding on iOS Safari */
@supports (padding: max(0px)) {
  .safe-area-bottom {
    padding-bottom: max(env(safe-area-inset-bottom), calc(var(--spacing) * 4));
  }
}

.webkit-overflow-scrolling-touch {
  -webkit-overflow-scrolling: touch;
}

/* Hide scrollbar during transition */
.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* WebKit */
}
</style>
